import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { crewErrorCode, describeCrewError } from '@/lib/jobs/crew-errors';
import { jobKeys } from '@/lib/queryKeys';
import type { ScheduleConflict } from '@/lib/scheduler/scheduleConflicts';
import type { AppointmentCrewRole } from '@/types/jobAppointment';

/** One conflict reported for a BLOCKED visit (the shared engine shape). */
export type BatchAssignConflict = ScheduleConflict;

/** Per-visit outcome of a batch crew assignment (partial success). */
export interface BatchAssignResult {
  appointmentId: string;
  status: 'ASSIGNED' | 'BLOCKED' | 'ERROR';
  conflicts?: BatchAssignConflict[];
  message?: string;
}

export interface BatchAssignResponse {
  results: BatchAssignResult[];
  assignedCount: number;
  blockedCount: number;
}

export interface BatchAssignPayload {
  appointmentIds: string[];
  crew: { technicianId: string; role: AppointmentCrewRole }[];
  /** Saved crew id (lead = Crew.leadTechnicianId) — same as the single assign. */
  crewId?: string;
  /** Acknowledge the flagged visits' conflicts and assign anyway (retry path). */
  overrideConflicts?: boolean;
}

/**
 * GAP-01 — assign one crew to several visits of a recurring job in a single
 * request. Partial success: the response reports each visit as ASSIGNED /
 * BLOCKED / ERROR so the caller can surface conflicts and offer an override.
 * Reuses `crewErrorCode`/`describeCrewError` for transport-level failures, the
 * same as {@link ReassignCrewDialog}.
 */
export function useBatchAssignCrew(jobId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<BatchAssignResponse, BatchAssignPayload>({
    mutationFn: (payload) =>
      apiPost<BatchAssignResponse>(
        operationsClient,
        jobEndpoints.appointmentsAssignBatch(jobId),
        payload,
      ),
    // Refresh only — the toast is the sheet's call: it shows a success toast when a
    // FULL success closes it, and stays silent on a partial/failed result (the inline
    // per-visit detail already communicates it), so the message isn't duplicated.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.appointments(jobId) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
    },
    showErrorToast: false,
    onError: (error) => {
      const code = crewErrorCode(error);
      toast.error(code ? describeCrewError(error) : error.message);
    },
  });
}
